/**
 * Browser-only helpers for the redirect round trip.
 *
 * Paying leaves your SPA entirely (SlickPay -> SATIM -> back to your returnUrl),
 * so anything the page was holding in memory is gone by the time the payer
 * comes back. These helpers park what you need to resume in localStorage.
 */

const isBrowser = () => typeof window !== "undefined" && typeof window.localStorage !== "undefined";

/**
 * Stores the in-flight payment (invoice id, resume url, plus whatever form data
 * you need to submit once payment is confirmed) across the SATIM redirect.
 *
 * Use a distinct `key` per flow so two flows can't clobber each other.
 */
export class PendingPayment {
  constructor(key = "slickpay:pending", storage) {
    this.key = key;
    this.storage = storage ?? (isBrowser() ? window.localStorage : null);
  }

  /** @param {{invoiceId: string|number, paymentUrl: string, [k: string]: unknown}} payment */
  save(payment) {
    this.storage?.setItem(this.key, JSON.stringify(payment));
    return payment;
  }

  /** @returns {object|null} the stored payment, or null if there is none/it is corrupt. */
  load() {
    const raw = this.storage?.getItem(this.key);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      this.clear();
      return null;
    }
  }

  clear() {
    this.storage?.removeItem(this.key);
  }
}

/** Reads a File/Blob as a data: URL, so it survives the redirect inside localStorage. */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error ?? new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

/** Rebuilds a File from a data: URL produced by {@link fileToBase64}. */
export function base64ToFile(base64, filename, fallbackMime = "image/jpeg") {
  const [header, data] = String(base64).split(",");
  const mime = /:(.*?);/.exec(header ?? "")?.[1] ?? fallbackMime;
  const binary = atob(data ?? "");
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], filename, { type: mime });
}
